import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useSettings } from '../contexts/SettingsContext';
import { useGym } from '../contexts/GymContext';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import api from '../services/api';
import { 
  Settings, 
  Palette, 
  Sun, 
  Moon, 
  Layout, 
  Bell, 
  BarChart3, 
  Target,
  Type,
  RefreshCw,
  Check,
  Monitor,
  Tags,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  User,
  Lock,
  Eye,
  EyeOff,
  Play,
  HelpCircle,
  Database,
  AlertTriangle,
  ShoppingCart,
  Receipt,
  Landmark,
  FileText,
  ArrowRight,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import { RegrasOFXManager } from '../components/RegrasOFXManager';

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { 
    settings, 
    updateSettings, 
    updateNestedSettings, 
    resetSettings,
    themes,
    colorPalettes,
    getCurrentPalette
  } = useSettings();
  const { selectedGym } = useGym();
  const { user } = useAuth();
  const { startOnboarding, hasCompletedOnboarding } = useOnboarding();
  
  const [activeTab, setActiveTab] = useState('appearance');
  
  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Expense Categories State
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loadingExpenseCategories, setLoadingExpenseCategories] = useState(false);
  const [showAddExpenseCategory, setShowAddExpenseCategory] = useState(false);
  const [editingExpenseCategory, setEditingExpenseCategory] = useState(null);
  const [newExpenseCategory, setNewExpenseCategory] = useState({ name: '', color: '#EF4444', tipo_despesa: 'operacional' });
  const [savingExpenseCategory, setSavingExpenseCategory] = useState(false);

  // Revenue Source Categories State
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [loadingRevenueCategories, setLoadingRevenueCategories] = useState(false);
  const [showAddRevenueCategory, setShowAddRevenueCategory] = useState(false);
  const [editingRevenueCategory, setEditingRevenueCategory] = useState(null);
  const [newRevenueCategory, setNewRevenueCategory] = useState({ name: '', color: '#22C55E' });
  const [savingRevenueCategory, setSavingRevenueCategory] = useState(false);

  // Investment Categories State
  const [investmentCategories, setInvestmentCategories] = useState([]);
  const [loadingInvestmentCategories, setLoadingInvestmentCategories] = useState(false);
  const [showAddInvestmentCategory, setShowAddInvestmentCategory] = useState(false);
  const [newInvestmentCategory, setNewInvestmentCategory] = useState({ name: '', color: '#D4A43A' });
  const [savingInvestmentCategory, setSavingInvestmentCategory] = useState(false);

  // Goal Categories State
  const [goalCategories, setGoalCategories] = useState([]);
  const [loadingGoalCategories, setLoadingGoalCategories] = useState(false);
  const [showAddGoalCategory, setShowAddGoalCategory] = useState(false);
  const [newGoalCategory, setNewGoalCategory] = useState({ name: '', color: '#22C55E', is_financial: false });
  const [savingGoalCategory, setSavingGoalCategory] = useState(false);

  // Limpar Dados State
  const [limpandoDados, setLimpandoDados] = useState(false);
  const [confirmacaoLimpar, setConfirmacaoLimpar] = useState('');
  const [dadosParaLimpar, setDadosParaLimpar] = useState({
    revenues: false,
    expenses: false,
    movimentacoes: false,
    contas: false,
    compromissos: false,
    metas: false,
    roi: false
  });

  const tabs = [
    { id: 'account', label: 'Minha Conta', icon: User },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'categories', label: 'Categorias', icon: Tags },
    { id: 'dashboard', label: 'Dashboard', icon: Layout },
    { id: 'goals', label: 'Metas', icon: Target },
    { id: 'charts', label: 'Gráficos', icon: BarChart3 },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'accessibility', label: 'Acessibilidade', icon: Type },
    { id: 'ofx-regras', label: 'Regras OFX', icon: Wand2 },
    { id: 'data', label: 'Dados', icon: Database },
    { id: 'help', label: 'Ajuda', icon: HelpCircle },
  ];

  // PASSWORD CHANGE HANDLER
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      toast.error('Digite sua senha atual');
      return;
    }
    if (!passwordData.newPassword) {
      toast.error('Digite a nova senha');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  // EXPENSE CATEGORIES HANDLERS
  const loadExpenseCategories = useCallback(async () => {
    setLoadingExpenseCategories(true);
    try {
      const params = selectedGym ? { gym_id: selectedGym.id } : {};
      const response = await api.get('/expense-categories', { params });
      setExpenseCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias de despesas');
    } finally {
      setLoadingExpenseCategories(false);
    }
  }, [selectedGym]);

  const handleAddExpenseCategory = async () => {
    if (!newExpenseCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingExpenseCategory(true);
    try {
      const response = await api.post('/expense-categories', {
        name: newExpenseCategory.name,
        color: newExpenseCategory.color,
        gym_id: selectedGym?.id || null,
        tipo_despesa: newExpenseCategory.tipo_despesa
      });
      setExpenseCategories([...expenseCategories, response.data]);
      setNewExpenseCategory({ name: '', color: '#EF4444', tipo_despesa: 'operacional' });
      setShowAddExpenseCategory(false);
      toast.success('Categoria criada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar categoria');
    } finally {
      setSavingExpenseCategory(false);
    }
  };

  const handleUpdateExpenseCategory = async () => {
    if (!editingExpenseCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingExpenseCategory(true);
    try {
      const response = await api.put(`/expense-categories/${editingExpenseCategory.id}`, {
        name: editingExpenseCategory.name,
        color: editingExpenseCategory.color,
        tipo_despesa: editingExpenseCategory.tipo_despesa || (editingExpenseCategory.is_operacional !== false ? 'operacional' : 'investimento')
      });
      setExpenseCategories(expenseCategories.map(c => c.id === editingExpenseCategory.id ? response.data : c));
      setEditingExpenseCategory(null);
      toast.success('Categoria atualizada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar categoria');
    } finally {
      setSavingExpenseCategory(false);
    }
  };

  const handleDeleteExpenseCategory = async (category) => {
    if (!window.confirm(`Deseja realmente excluir a categoria "${category.name}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/expense-categories/${category.id}`);
      setExpenseCategories(expenseCategories.filter(c => c.id !== category.id));
      toast.success('Categoria excluída!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  // REVENUE CATEGORIES HANDLERS
  const loadRevenueCategories = useCallback(async () => {
    setLoadingRevenueCategories(true);
    try {
      const params = selectedGym ? { gym_id: selectedGym.id } : {};
      const response = await api.get('/revenue-source-categories', { params });
      setRevenueCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias de origem');
    } finally {
      setLoadingRevenueCategories(false);
    }
  }, [selectedGym]);

  const handleAddRevenueCategory = async () => {
    if (!newRevenueCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingRevenueCategory(true);
    try {
      const response = await api.post('/revenue-source-categories', {
        name: newRevenueCategory.name,
        color: newRevenueCategory.color,
        gym_id: selectedGym?.id || null
      });
      setRevenueCategories([...revenueCategories, response.data]);
      setNewRevenueCategory({ name: '', color: '#22C55E' });
      setShowAddRevenueCategory(false);
      toast.success('Categoria criada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar categoria');
    } finally {
      setSavingRevenueCategory(false);
    }
  };

  const handleUpdateRevenueCategory = async () => {
    if (!editingRevenueCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingRevenueCategory(true);
    try {
      const response = await api.put(`/revenue-source-categories/${editingRevenueCategory.id}`, {
        name: editingRevenueCategory.name,
        color: editingRevenueCategory.color
      });
      setRevenueCategories(revenueCategories.map(c => c.id === editingRevenueCategory.id ? response.data : c));
      setEditingRevenueCategory(null);
      toast.success('Categoria atualizada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar categoria');
    } finally {
      setSavingRevenueCategory(false);
    }
  };

  const handleDeleteRevenueCategory = async (category) => {
    if (!window.confirm(`Deseja realmente excluir a categoria "${category.name}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/revenue-source-categories/${category.id}`);
      setRevenueCategories(revenueCategories.filter(c => c.id !== category.id));
      toast.success('Categoria excluída!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  // INVESTMENT CATEGORIES HANDLERS
  const loadInvestmentCategories = useCallback(async () => {
    setLoadingInvestmentCategories(true);
    try {
      const params = selectedGym ? { gym_id: selectedGym.id } : {};
      const response = await api.get('/investment-categories', { params });
      setInvestmentCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias de investimento');
    } finally {
      setLoadingInvestmentCategories(false);
    }
  }, [selectedGym]);

  const handleAddInvestmentCategory = async () => {
    if (!newInvestmentCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingInvestmentCategory(true);
    try {
      const response = await api.post('/investment-categories', {
        name: newInvestmentCategory.name,
        color: newInvestmentCategory.color,
        gym_id: selectedGym?.id || null
      });
      setInvestmentCategories([...investmentCategories, response.data]);
      setNewInvestmentCategory({ name: '', color: '#D4A43A' });
      setShowAddInvestmentCategory(false);
      toast.success('Categoria de investimento criada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar categoria');
    } finally {
      setSavingInvestmentCategory(false);
    }
  };

  const handleDeleteInvestmentCategory = async (category) => {
    if (!window.confirm(`Deseja realmente excluir a categoria "${category.name}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/investment-categories/${category.id}`);
      setInvestmentCategories(investmentCategories.filter(c => c.id !== category.id));
      toast.success('Categoria excluída!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  // GOAL CATEGORIES HANDLERS
  const loadGoalCategories = useCallback(async () => {
    setLoadingGoalCategories(true);
    try {
      const params = selectedGym ? { gym_id: selectedGym.id } : {};
      const response = await api.get('/goal-categories', { params });
      setGoalCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias de metas');
    } finally {
      setLoadingGoalCategories(false);
    }
  }, [selectedGym]);

  // Load categories when tab is selected (declarado apos os 4 useCallback acima para evitar TDZ)
  useEffect(() => {
    if (activeTab === 'categories') {
      loadExpenseCategories();
      loadRevenueCategories();
      loadInvestmentCategories();
      loadGoalCategories();
    }
  }, [activeTab, selectedGym, loadExpenseCategories, loadRevenueCategories, loadInvestmentCategories, loadGoalCategories]);

  const handleAddGoalCategory = async () => {
    if (!newGoalCategory.name.trim()) {
      toast.error('Digite o nome da categoria');
      return;
    }
    
    setSavingGoalCategory(true);
    try {
      const response = await api.post('/goal-categories', {
        name: newGoalCategory.name,
        color: newGoalCategory.color,
        is_financial: newGoalCategory.is_financial,
        gym_id: selectedGym?.id || null
      });
      setGoalCategories([...goalCategories, response.data]);
      setNewGoalCategory({ name: '', color: '#22C55E', is_financial: false });
      setShowAddGoalCategory(false);
      toast.success('Categoria de meta criada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar categoria');
    } finally {
      setSavingGoalCategory(false);
    }
  };

  const handleDeleteGoalCategory = async (category) => {
    if (category.is_default) {
      toast.error('Não é possível excluir categorias padrão');
      return;
    }
    
    if (!window.confirm(`Excluir a categoria "${category.name}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/goal-categories/${category.id}`);
      setGoalCategories(goalCategories.filter(c => c.id !== category.id));
      toast.success('Categoria excluída!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  const handleReset = () => {
    if (window.confirm('Tem certeza que deseja restaurar todas as configurações para o padrão?')) {
      resetSettings();
      toast.success('Configurações restauradas!');
    }
  };

  const currentPalette = getCurrentPalette();

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight flex items-center gap-3">
              <Settings className="text-[var(--color-primary)]" size={48} />
              Configurações
            </h1>
            <p className="text-zinc-400 mt-2">Personalize sua experiência no Alvo</p>
          </div>
          <Button
            onClick={handleReset}
            variant="outline"
            className="flex items-center gap-2 border-zinc-700 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            <RefreshCw size={18} />
            Restaurar Padrão
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tabs Sidebar */}
          <Card className="glass-panel lg:col-span-1">
            <CardContent className="p-4">
              <nav className="space-y-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        activeTab === tab.id
                          ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border border-[var(--color-primary)]/20'
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Minha Conta */}
            {activeTab === 'account' && (
              <>
                {/* Informações do Usuário */}
                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <User size={24} className="text-[var(--color-primary)]" />
                      Informações da Conta
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-black/20 border border-white/5">
                        <p className="text-sm text-zinc-400 mb-1">Nome</p>
                        <p className="font-medium text-white">{user?.name || 'N/A'}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-black/20 border border-white/5">
                        <p className="text-sm text-zinc-400 mb-1">Email</p>
                        <p className="font-medium text-white">{user?.email || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Alterar Senha */}
                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <Lock size={24} className="text-[var(--color-primary)]" />
                      Alterar Senha
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4 max-w-md">
                      {/* Senha Atual */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Senha Atual</Label>
                        <div className="relative">
                          <Input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            placeholder="Digite sua senha atual"
                            className="bg-black/30 border-white/10 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                          >
                            {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Nova Senha */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Nova Senha</Label>
                        <div className="relative">
                          <Input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            placeholder="Digite a nova senha (mín. 6 caracteres)"
                            className="bg-black/30 border-white/10 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                          >
                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Confirmar Nova Senha */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Confirmar Nova Senha</Label>
                        <Input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          placeholder="Confirme a nova senha"
                          className="bg-black/30 border-white/10"
                        />
                        {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                          <p className="text-xs text-red-400">As senhas não coincidem</p>
                        )}
                      </div>

                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                        className="w-full bg-[var(--color-primary)] text-black hover:bg-[var(--color-primary)]/90"
                      >
                        {changingPassword ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Alterando...
                          </>
                        ) : (
                          <>
                            <Lock className="mr-2 h-4 w-4" />
                            Alterar Senha
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Aparência */}
            {activeTab === 'appearance' && (
              <>
                {/* Tema */}
                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <Monitor size={24} className="text-[var(--color-primary)]" />
                      Tema
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(themes).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => updateSettings('theme', key)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            settings.theme === key
                              ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                              : 'border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div 
                            className="w-full h-16 rounded-lg mb-3 flex items-center justify-center"
                            style={{ backgroundColor: theme.background }}
                          >
                            {key === 'dark' && <Moon className="text-white" size={24} />}
                            {key === 'light' && <Sun className="text-zinc-800" size={24} />}
                            {key === 'midnight' && <Moon className="text-blue-400" size={24} />}
                          </div>
                          <p className={`font-medium ${settings.theme === key ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                            {theme.name}
                          </p>
                          {settings.theme === key && (
                            <Check className="text-[var(--color-primary)] mx-auto mt-2" size={18} />
                          )}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Paleta de Cores */}
                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <Palette size={24} className="text-[var(--color-primary)]" />
                      Paleta de Cores
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(colorPalettes).filter(([key]) => key !== 'custom').map(([key, palette]) => (
                        <button
                          key={key}
                          onClick={() => updateSettings('colorPalette', key)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            settings.colorPalette === key
                              ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                              : 'border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div className="flex gap-2 mb-3">
                            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: palette.primary }} />
                            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: palette.secondary }} />
                            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: palette.danger }} />
                            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: palette.warning }} />
                          </div>
                          <p className={`font-medium text-sm ${settings.colorPalette === key ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                            {palette.name}
                          </p>
                        </button>
                      ))}
                    </div>

                    {/* Cores Personalizadas */}
                    <div className="border-t border-white/10 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-white font-medium">Cores Personalizadas</Label>
                        <Button
                          variant={settings.colorPalette === 'custom' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateSettings('colorPalette', 'custom')}
                          className={settings.colorPalette === 'custom' ? 'bg-[var(--color-primary)] text-black' : ''}
                        >
                          {settings.colorPalette === 'custom' ? 'Ativo' : 'Ativar'}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Primária</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={settings.customColors.primary}
                              onChange={(e) => updateNestedSettings('customColors', 'primary', e.target.value)}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={settings.customColors.primary}
                              onChange={(e) => updateNestedSettings('customColors', 'primary', e.target.value)}
                              className="input-field h-10"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Secundária</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={settings.customColors.secondary}
                              onChange={(e) => updateNestedSettings('customColors', 'secondary', e.target.value)}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={settings.customColors.secondary}
                              onChange={(e) => updateNestedSettings('customColors', 'secondary', e.target.value)}
                              className="input-field h-10"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Perigo</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={settings.customColors.danger}
                              onChange={(e) => updateNestedSettings('customColors', 'danger', e.target.value)}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={settings.customColors.danger}
                              onChange={(e) => updateNestedSettings('customColors', 'danger', e.target.value)}
                              className="input-field h-10"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Aviso</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={settings.customColors.warning}
                              onChange={(e) => updateNestedSettings('customColors', 'warning', e.target.value)}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={settings.customColors.warning}
                              onChange={(e) => updateNestedSettings('customColors', 'warning', e.target.value)}
                              className="input-field h-10"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Categorias */}
            {activeTab === 'categories' && (
              <div className="space-y-6">
                {/* Categorias de Despesas */}
                <Card className="glass-panel">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Tags size={24} className="text-[#EF4444]" />
                        Categorias de Despesas
                      </CardTitle>
                      <Button
                        onClick={() => setShowAddExpenseCategory(true)}
                        className="bg-[#EF4444] hover:bg-[#EF4444]/90 text-white"
                        size="sm"
                        data-testid="add-expense-category-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Nova Categoria
                      </Button>
                    </div>
                    <p className="text-zinc-400 text-sm mt-2">
                      Personalize as categorias de despesas para seu negócio
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Expense Category Form */}
                    {showAddExpenseCategory && (
                      <div className="p-4 rounded-xl bg-[#EF4444]/5 border border-[#EF4444]/20 space-y-4" data-testid="add-expense-category-form">
                        <div className="flex items-center justify-between">
                          <h4 className="text-white font-medium">Nova Categoria de Despesa</h4>
                          <button onClick={() => setShowAddExpenseCategory(false)} className="text-zinc-400 hover:text-white">
                            <X size={18} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-zinc-400 text-sm">Nome</Label>
                            <Input
                              value={newExpenseCategory.name}
                              onChange={(e) => setNewExpenseCategory({ ...newExpenseCategory, name: e.target.value })}
                              placeholder="Ex: Marketing Digital"
                              className="input-field mt-1"
                              data-testid="expense-category-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-sm">Cor</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={newExpenseCategory.color}
                                onChange={(e) => setNewExpenseCategory({ ...newExpenseCategory, color: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                              />
                              <Input
                                value={newExpenseCategory.color}
                                onChange={(e) => setNewExpenseCategory({ ...newExpenseCategory, color: e.target.value })}
                                className="input-field h-10"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-sm">Tipo</Label>
                            <div className="flex gap-2 mt-2">
                              {[
                                { v: 'operacional', label: 'Operacional', active: 'bg-emerald-500/20 border border-emerald-500 text-emerald-400' },
                                { v: 'pontual', label: 'Pontual', active: 'bg-amber-500/20 border border-amber-500 text-amber-400' },
                                { v: 'investimento', label: 'Investimento', active: 'bg-blue-500/20 border border-blue-500 text-blue-400' },
                              ].map(opt => (
                                <button
                                  key={opt.v}
                                  type="button"
                                  onClick={() => setNewExpenseCategory({ ...newExpenseCategory, tipo_despesa: opt.v })}
                                  data-testid={`new-expense-tipo-${opt.v}`}
                                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                                    newExpenseCategory.tipo_despesa === opt.v
                                      ? opt.active
                                      : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500">
                          <b className="text-emerald-400">Operacional</b> entra no DRE e na Projeção. <b className="text-amber-400">Pontual</b> e <b className="text-blue-400">Investimento</b> aparecem só no Fluxo de Caixa (não projetam nem entram no DRE).
                        </p>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddExpenseCategory(false)} className="border-zinc-700">
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleAddExpenseCategory}
                            disabled={savingExpenseCategory}
                            className="bg-[#EF4444] hover:bg-[#EF4444]/90 text-white"
                            data-testid="save-expense-category-btn"
                          >
                            {savingExpenseCategory ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                            Salvar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expense Categories List */}
                    {loadingExpenseCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={32} className="animate-spin text-[#EF4444]" />
                      </div>
                    ) : expenseCategories.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <Tags size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Nenhuma categoria encontrada</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {expenseCategories.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
                            data-testid={`expense-category-item-${category.id}`}
                          >
                            {editingExpenseCategory?.id === category.id ? (
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    value={editingExpenseCategory.color || '#EF4444'}
                                    onChange={(e) => setEditingExpenseCategory({ ...editingExpenseCategory, color: e.target.value })}
                                    className="w-8 h-8 rounded cursor-pointer"
                                  />
                                  <Input
                                    value={editingExpenseCategory.name}
                                    onChange={(e) => setEditingExpenseCategory({ ...editingExpenseCategory, name: e.target.value })}
                                    className="input-field h-8 flex-1"
                                    autoFocus
                                  />
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {[
                                    { v: 'operacional', label: 'Operacional', active: 'bg-emerald-500/20 border border-emerald-500 text-emerald-400' },
                                    { v: 'pontual', label: 'Pontual', active: 'bg-amber-500/20 border border-amber-500 text-amber-400' },
                                    { v: 'investimento', label: 'Investimento', active: 'bg-blue-500/20 border border-blue-500 text-blue-400' },
                                  ].map(opt => {
                                    const cur = editingExpenseCategory.tipo_despesa || (editingExpenseCategory.is_operacional !== false ? 'operacional' : 'investimento');
                                    return (
                                      <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setEditingExpenseCategory({ ...editingExpenseCategory, tipo_despesa: opt.v })}
                                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                          cur === opt.v ? opt.active : 'bg-zinc-800 border border-zinc-700 text-zinc-400'
                                        }`}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                  <div className="flex-1" />
                                  <Button size="sm" onClick={handleUpdateExpenseCategory} disabled={savingExpenseCategory} className="bg-[#EF4444] text-white h-7 px-2">
                                    {savingExpenseCategory ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingExpenseCategory(null)} className="h-7 px-2 border-zinc-700">
                                    <X size={14} />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#EF4444' }} />
                                  <span className="text-white text-sm font-medium">{category.name}</span>
                                  {category.is_default && (
                                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400 uppercase">Padrão</span>
                                  )}
                                  {(() => {
                                    const tipo = category.tipo_despesa || (category.is_operacional !== false ? 'operacional' : 'investimento');
                                    const badge = {
                                      operacional: { label: 'Operacional', cls: 'bg-emerald-500/20 text-emerald-400' },
                                      pontual: { label: 'Pontual', cls: 'bg-amber-500/20 text-amber-400' },
                                      investimento: { label: 'Investimento', cls: 'bg-blue-500/20 text-blue-400' },
                                    }[tipo] || { label: tipo, cls: 'bg-zinc-500/20 text-zinc-400' };
                                    return (
                                      <span className={`text-[8px] px-1.5 py-0.5 rounded uppercase whitespace-nowrap ${badge.cls}`}>
                                        {badge.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="sm" onClick={() => setEditingExpenseCategory(category)} className="h-7 w-7 p-0 text-zinc-400 hover:text-white">
                                    <Pencil size={12} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteExpenseCategory(category)} className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400">
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Categorias de Origem de Receita */}
                <Card className="glass-panel">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Tags size={24} className="text-[#22C55E]" />
                        Categorias de Origem de Receita
                      </CardTitle>
                      <Button
                        onClick={() => setShowAddRevenueCategory(true)}
                        className="bg-[#22C55E] hover:bg-[#22C55E]/90 text-black"
                        size="sm"
                        data-testid="add-revenue-category-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Nova Categoria
                      </Button>
                    </div>
                    <p className="text-zinc-400 text-sm mt-2">
                      Personalize as origens de receita do seu negócio
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Revenue Category Form */}
                    {showAddRevenueCategory && (
                      <div className="p-4 rounded-xl bg-[#22C55E]/5 border border-[#22C55E]/20 space-y-4" data-testid="add-revenue-category-form">
                        <div className="flex items-center justify-between">
                          <h4 className="text-white font-medium">Nova Categoria de Origem</h4>
                          <button onClick={() => setShowAddRevenueCategory(false)} className="text-zinc-400 hover:text-white">
                            <X size={18} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-zinc-400 text-sm">Nome</Label>
                            <Input
                              value={newRevenueCategory.name}
                              onChange={(e) => setNewRevenueCategory({ ...newRevenueCategory, name: e.target.value })}
                              placeholder="Ex: E-commerce"
                              className="input-field mt-1"
                              data-testid="revenue-category-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-sm">Cor</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={newRevenueCategory.color}
                                onChange={(e) => setNewRevenueCategory({ ...newRevenueCategory, color: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                              />
                              <Input
                                value={newRevenueCategory.color}
                                onChange={(e) => setNewRevenueCategory({ ...newRevenueCategory, color: e.target.value })}
                                className="input-field h-10"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddRevenueCategory(false)} className="border-zinc-700">
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleAddRevenueCategory}
                            disabled={savingRevenueCategory}
                            className="bg-[#22C55E] hover:bg-[#22C55E]/90 text-black"
                            data-testid="save-revenue-category-btn"
                          >
                            {savingRevenueCategory ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                            Salvar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Revenue Categories List */}
                    {loadingRevenueCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={32} className="animate-spin text-[#22C55E]" />
                      </div>
                    ) : revenueCategories.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <Tags size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Nenhuma categoria encontrada</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {revenueCategories.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
                            data-testid={`revenue-category-item-${category.id}`}
                          >
                            {editingRevenueCategory?.id === category.id ? (
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  type="color"
                                  value={editingRevenueCategory.color || '#22C55E'}
                                  onChange={(e) => setEditingRevenueCategory({ ...editingRevenueCategory, color: e.target.value })}
                                  className="w-8 h-8 rounded cursor-pointer"
                                />
                                <Input
                                  value={editingRevenueCategory.name}
                                  onChange={(e) => setEditingRevenueCategory({ ...editingRevenueCategory, name: e.target.value })}
                                  className="input-field h-8 flex-1"
                                  autoFocus
                                />
                                <Button size="sm" onClick={handleUpdateRevenueCategory} disabled={savingRevenueCategory} className="bg-[#22C55E] text-black h-8 px-2">
                                  {savingRevenueCategory ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingRevenueCategory(null)} className="h-8 px-2 border-zinc-700">
                                  <X size={14} />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#22C55E' }} />
                                  <span className="text-white text-sm font-medium">{category.name}</span>
                                  {category.is_default && (
                                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400 uppercase">Padrão</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="sm" onClick={() => setEditingRevenueCategory(category)} className="h-7 w-7 p-0 text-zinc-400 hover:text-white">
                                    <Pencil size={12} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteRevenueCategory(category)} className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400">
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Categorias de Investimento */}
                <Card className="glass-panel border-white/10">
                  <CardHeader className="border-b border-white/10 pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Tags size={24} className="text-[#D4A43A]" />
                        Categorias de Investimento (ROI)
                      </CardTitle>
                      <Button
                        onClick={() => setShowAddInvestmentCategory(!showAddInvestmentCategory)}
                        className="bg-[#D4A43A] hover:bg-[#D4A43A]/90 text-black"
                        data-testid="add-investment-category-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Adicionar
                      </Button>
                    </div>
                    <p className="text-zinc-500 text-sm mt-1">
                      Categorias para registrar investimentos (terreno, obra, equipamentos, etc.)
                    </p>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    {/* Add Investment Category Form */}
                    {showAddInvestmentCategory && (
                      <div className="p-4 rounded-xl bg-[#D4A43A]/10 border border-[#D4A43A]/30 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-zinc-300">Nome da Categoria</Label>
                            <Input
                              value={newInvestmentCategory.name}
                              onChange={(e) => setNewInvestmentCategory({ ...newInvestmentCategory, name: e.target.value })}
                              placeholder="Ex: Terreno, Obra, Equipamentos..."
                              className="input-field mt-1"
                              data-testid="new-investment-category-name"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Cor</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={newInvestmentCategory.color}
                                onChange={(e) => setNewInvestmentCategory({ ...newInvestmentCategory, color: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                              />
                              <Input
                                value={newInvestmentCategory.color}
                                onChange={(e) => setNewInvestmentCategory({ ...newInvestmentCategory, color: e.target.value })}
                                className="input-field h-10"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddInvestmentCategory(false)} className="border-zinc-700">
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleAddInvestmentCategory}
                            disabled={savingInvestmentCategory}
                            className="bg-[#D4A43A] hover:bg-[#D4A43A]/90 text-black"
                            data-testid="save-investment-category-btn"
                          >
                            {savingInvestmentCategory ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                            Salvar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Investment Categories List */}
                    {loadingInvestmentCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={32} className="animate-spin text-[#D4A43A]" />
                      </div>
                    ) : investmentCategories.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <Tags size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Nenhuma categoria encontrada</p>
                        <p className="text-sm mt-1">Adicione categorias como: Terreno, Obra, Equipamentos, Móveis, etc.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {investmentCategories.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
                            data-testid={`investment-category-item-${category.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#D4A43A' }} />
                              <span className="text-white text-sm font-medium">{category.name}</span>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteInvestmentCategory(category)}
                                className="h-8 px-2 text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/20"
                                data-testid={`delete-investment-category-${category.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Categorias de Metas */}
                <Card className="glass-panel">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl text-white flex items-center gap-2">
                          <Target size={24} className="text-[#22C55E]" />
                          Categorias de Metas
                        </CardTitle>
                        <p className="text-zinc-500 text-sm mt-1">
                          Tipos de metas disponíveis para acompanhamento
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowAddGoalCategory(true)}
                        className="bg-[#22C55E] hover:bg-[#22C55E]/90 text-black"
                        size="sm"
                        data-testid="add-goal-category-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Nova Categoria
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    {/* Add Goal Category Form */}
                    {showAddGoalCategory && (
                      <div className="p-4 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/30 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-zinc-300">Nome da Categoria</Label>
                            <Input
                              value={newGoalCategory.name}
                              onChange={(e) => setNewGoalCategory({ ...newGoalCategory, name: e.target.value })}
                              placeholder="Ex: Novos Clientes, Retenção..."
                              className="input-field mt-1"
                              data-testid="new-goal-category-name"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Cor</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={newGoalCategory.color}
                                onChange={(e) => setNewGoalCategory({ ...newGoalCategory, color: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                              />
                              <Input
                                value={newGoalCategory.color}
                                onChange={(e) => setNewGoalCategory({ ...newGoalCategory, color: e.target.value })}
                                className="input-field h-10"
                              />
                            </div>
                          </div>
                          <div className="flex items-end">
                            <div className="flex items-center gap-3 h-10">
                              <Switch
                                id="is-financial"
                                checked={newGoalCategory.is_financial}
                                onCheckedChange={(checked) => setNewGoalCategory({ ...newGoalCategory, is_financial: checked })}
                              />
                              <Label htmlFor="is-financial" className="text-zinc-300 text-sm">
                                Valor calculado automaticamente
                              </Label>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddGoalCategory(false)} className="border-zinc-700">
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleAddGoalCategory}
                            disabled={savingGoalCategory}
                            className="bg-[#22C55E] hover:bg-[#22C55E]/90 text-black"
                            data-testid="save-goal-category-btn"
                          >
                            {savingGoalCategory ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                            Salvar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Goal Categories List */}
                    {loadingGoalCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={32} className="animate-spin text-[#22C55E]" />
                      </div>
                    ) : goalCategories.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <Target size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Nenhuma categoria encontrada</p>
                        <p className="text-sm mt-1">Categorias padrão serão criadas ao carregar</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {goalCategories.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
                            data-testid={`goal-category-item-${category.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#22C55E' }} />
                              <span className="text-white text-sm font-medium">{category.name}</span>
                              {category.is_default && (
                                <span className="text-[8px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400 uppercase">Padrão</span>
                              )}
                              {category.is_financial && (
                                <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#22C55E]/20 text-[#22C55E] uppercase">Auto</span>
                              )}
                            </div>
                            {!category.is_default && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteGoalCategory(category)}
                                  className="h-8 px-2 text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/20"
                                  data-testid={`delete-goal-category-${category.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Dashboard */}
            {activeTab === 'dashboard' && (
              <>
                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <Layout size={24} className="text-[var(--color-primary)]" />
                      Layout do Dashboard
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label className="text-zinc-400 mb-3 block">Estilo de Layout</Label>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { id: 'default', name: 'Padrão', desc: 'Layout balanceado' },
                          { id: 'compact', name: 'Compacto', desc: 'Mais informações em menos espaço' },
                          { id: 'expanded', name: 'Expandido', desc: 'Mais destaque para gráficos' },
                        ].map(layout => (
                          <button
                            key={layout.id}
                            onClick={() => updateSettings('dashboardLayout', layout.id)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              settings.dashboardLayout === layout.id
                                ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                                : 'border-white/10 hover:border-white/20'
                            }`}
                          >
                            <p className={`font-bold ${settings.dashboardLayout === layout.id ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                              {layout.name}
                            </p>
                            <p className="text-zinc-500 text-sm mt-1">{layout.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-white/10 pt-6">
                      <Label className="text-zinc-400 mb-3 block">Cards Visíveis no Dashboard</Label>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { key: 'revenue', label: 'Faturamento' },
                          { key: 'expenses', label: 'Despesas' },
                          { key: 'profit', label: 'Lucro' },
                          { key: 'pendingBills', label: 'Boletos Pendentes' },
                          { key: 'monthlyTrend', label: 'Tendência Mensal' },
                          { key: 'expensesByCategory', label: 'Despesas por Categoria' },
                          { key: 'revenueBySource', label: 'Receitas por Origem' },
                          { key: 'goals', label: 'Metas do Mês' },
                        ].map(card => (
                          <div key={card.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                            <span className="text-white">{card.label}</span>
                            <Switch
                              checked={settings.cardsToShow[card.key]}
                              onCheckedChange={(checked) => updateNestedSettings('cardsToShow', card.key, checked)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-white/10 pt-6">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div>
                          <p className="text-white font-medium">Mensagem de Boas-vindas</p>
                          <p className="text-zinc-500 text-sm">Mostrar saudação personalizada no dashboard</p>
                        </div>
                        <Switch
                          checked={settings.showWelcomeMessage}
                          onCheckedChange={(checked) => updateSettings('showWelcomeMessage', checked)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel">
                  <CardHeader>
                    <CardTitle className="text-xl text-white">Formato de Moeda</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Símbolo da Moeda</Label>
                        <Input
                          value={settings.currencySymbol}
                          onChange={(e) => updateSettings('currencySymbol', e.target.value)}
                          className="input-field mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Casas Decimais</Label>
                        <select
                          value={settings.decimalPlaces}
                          onChange={(e) => updateSettings('decimalPlaces', parseInt(e.target.value))}
                          className="input-field w-full mt-1"
                        >
                          <option value={0}>0</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                        </select>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-white/5">
                      <p className="text-zinc-400 text-sm mb-2">Preview:</p>
                      <p className="text-2xl font-bold text-[var(--color-primary)] font-numbers">
                        {settings.currencySymbol} 1{settings.thousandsSeparator}234{settings.decimalSeparator}{'5'.repeat(settings.decimalPlaces) || '00'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Metas */}
            {activeTab === 'goals' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Target size={24} className="text-[var(--color-primary)]" />
                    Configurações de Metas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-zinc-400 mb-3 block">Direção Padrão para Novas Metas</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => updateSettings('defaultGoalDirection', 'progressiva')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          settings.defaultGoalDirection === 'progressiva'
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <p className={`font-bold ${settings.defaultGoalDirection === 'progressiva' ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                          Progressiva
                        </p>
                        <p className="text-zinc-500 text-sm mt-1">Quer atingir ou ultrapassar</p>
                      </button>
                      <button
                        onClick={() => updateSettings('defaultGoalDirection', 'decrescente')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          settings.defaultGoalDirection === 'decrescente'
                            ? 'border-[var(--color-danger)] bg-[var(--color-danger)]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <p className={`font-bold ${settings.defaultGoalDirection === 'decrescente' ? 'text-[var(--color-danger)]' : 'text-white'}`}>
                          Decrescente
                        </p>
                        <p className="text-zinc-500 text-sm mt-1">Quer ficar abaixo do limite</p>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div>
                        <p className="text-white font-medium">Mostrar Porcentagem</p>
                        <p className="text-zinc-500 text-sm">Exibir % de progresso nas metas</p>
                      </div>
                      <Switch
                        checked={settings.showGoalPercentage}
                        onCheckedChange={(checked) => updateSettings('showGoalPercentage', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div>
                        <p className="text-white font-medium">Código de Cores</p>
                        <p className="text-zinc-500 text-sm">Colorir metas por status (verde, amarelo, vermelho)</p>
                      </div>
                      <Switch
                        checked={settings.goalColorCoding}
                        onCheckedChange={(checked) => updateSettings('goalColorCoding', checked)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gráficos */}
            {activeTab === 'charts' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <BarChart3 size={24} className="text-[var(--color-primary)]" />
                    Configurações de Gráficos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-zinc-400 mb-3 block">Estilo dos Gráficos</Label>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { id: 'modern', name: 'Moderno', desc: 'Gradientes e sombras' },
                        { id: 'classic', name: 'Clássico', desc: 'Cores sólidas' },
                        { id: 'minimal', name: 'Minimalista', desc: 'Linhas simples' },
                      ].map(style => (
                        <button
                          key={style.id}
                          onClick={() => updateSettings('chartStyle', style.id)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            settings.chartStyle === style.id
                              ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                              : 'border-white/10 hover:border-white/20'
                          }`}
                        >
                          <p className={`font-bold ${settings.chartStyle === style.id ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                            {style.name}
                          </p>
                          <p className="text-zinc-500 text-sm mt-1">{style.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Animações</p>
                      <p className="text-zinc-500 text-sm">Ativar animações nos gráficos</p>
                    </div>
                    <Switch
                      checked={settings.chartAnimations}
                      onCheckedChange={(checked) => updateSettings('chartAnimations', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notificações */}
            {activeTab === 'notifications' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Bell size={24} className="text-[var(--color-primary)]" />
                    Notificações
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Lembretes de Boletos</p>
                      <p className="text-zinc-500 text-sm">Avisar sobre boletos próximos do vencimento</p>
                    </div>
                    <Switch
                      checked={settings.notifications.billReminders}
                      onCheckedChange={(checked) => updateNestedSettings('notifications', 'billReminders', checked)}
                    />
                  </div>
                  
                  {settings.notifications.billReminders && (
                    <div className="ml-4 p-3 rounded-lg bg-white/5">
                      <Label className="text-zinc-400">Dias antes do vencimento</Label>
                      <select
                        value={settings.notifications.reminderDaysBefore}
                        onChange={(e) => updateNestedSettings('notifications', 'reminderDaysBefore', parseInt(e.target.value))}
                        className="input-field w-full mt-1"
                      >
                        <option value={1}>1 dia</option>
                        <option value={3}>3 dias</option>
                        <option value={5}>5 dias</option>
                        <option value={7}>7 dias</option>
                      </select>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Alertas de Metas</p>
                      <p className="text-zinc-500 text-sm">Notificar quando metas forem atingidas ou ultrapassadas</p>
                    </div>
                    <Switch
                      checked={settings.notifications.goalAlerts}
                      onCheckedChange={(checked) => updateNestedSettings('notifications', 'goalAlerts', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Relatório Mensal</p>
                      <p className="text-zinc-500 text-sm">Receber resumo no início de cada mês</p>
                    </div>
                    <Switch
                      checked={settings.notifications.monthlyReport}
                      onCheckedChange={(checked) => updateNestedSettings('notifications', 'monthlyReport', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Acessibilidade */}
            {activeTab === 'accessibility' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Type size={24} className="text-[var(--color-primary)]" />
                    Acessibilidade
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-zinc-400 mb-3 block">Tamanho da Fonte</Label>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { id: 'small', name: 'Pequeno', sample: 'Aa' },
                        { id: 'medium', name: 'Médio', sample: 'Aa' },
                        { id: 'large', name: 'Grande', sample: 'Aa' },
                      ].map(size => (
                        <button
                          key={size.id}
                          onClick={() => updateSettings('fontSize', size.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            settings.fontSize === size.id
                              ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10'
                              : 'border-white/10 hover:border-white/20'
                          }`}
                        >
                          <p className={`font-bold mb-2 ${
                            size.id === 'small' ? 'text-sm' : size.id === 'large' ? 'text-2xl' : 'text-base'
                          } ${settings.fontSize === size.id ? 'text-[var(--color-primary)]' : 'text-white'}`}>
                            {size.sample}
                          </p>
                          <p className={`text-sm ${settings.fontSize === size.id ? 'text-[var(--color-primary)]' : 'text-zinc-400'}`}>
                            {size.name}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Alto Contraste</p>
                      <p className="text-zinc-500 text-sm">Aumentar contraste para melhor legibilidade</p>
                    </div>
                    <Switch
                      checked={settings.highContrast}
                      onCheckedChange={(checked) => updateSettings('highContrast', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Regras OFX */}
            {activeTab === 'ofx-regras' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-emerald-400" />
                    Regras de Categorização OFX
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RegrasOFXManager gymId={selectedGym?.id} />
                </CardContent>
              </Card>
            )}

            {/* Dados */}
            {activeTab === 'data' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Database size={24} className="text-[var(--color-primary)]" />
                    Gerenciar Dados
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Info da Empresa Selecionada */}
                  {selectedGym && (
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-zinc-400 text-sm">Empresa selecionada:</p>
                      <p className="text-white font-semibold text-lg">{selectedGym.name}</p>
                    </div>
                  )}

                  {/* Limpar Dados */}
                  <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                      <div className="p-3 rounded-xl bg-red-500/20">
                        <AlertTriangle size={24} className="text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">Limpar Dados</h3>
                        <p className="text-zinc-400 text-sm mb-4">
                          Selecione quais dados deseja excluir permanentemente:
                        </p>
                        
                        {/* Checkboxes de seleção */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.revenues}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, revenues: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-revenues"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Receitas / Faturamento</p>
                              <p className="text-zinc-500 text-xs">DRE, fontes de receita</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.expenses}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, expenses: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-expenses"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Despesas</p>
                              <p className="text-zinc-500 text-xs">Operacionais e não-operacionais</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.movimentacoes}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, movimentacoes: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-movimentacoes"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Movimentações de Caixa</p>
                              <p className="text-zinc-500 text-xs">Lançamentos manuais e OFX</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.contas}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, contas: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-contas"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Contas Bancárias</p>
                              <p className="text-zinc-500 text-xs">Cadastro de contas</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.compromissos}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, compromissos: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-compromissos"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Compromissos</p>
                              <p className="text-zinc-500 text-xs">Financiamentos e parcelas</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.metas}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, metas: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-metas"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Metas</p>
                              <p className="text-zinc-500 text-xs">Metas financeiras</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={dadosParaLimpar.roi}
                              onChange={(e) => setDadosParaLimpar({...dadosParaLimpar, roi: e.target.checked})}
                              className="w-4 h-4 rounded border-zinc-600 text-red-500 focus:ring-red-500"
                              data-testid="limpar-dados-checkbox-roi"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">Investimentos ROI</p>
                              <p className="text-zinc-500 text-xs">Cálculos de retorno</p>
                            </div>
                          </label>
                        </div>
                        
                        {/* Botões rápidos */}
                        <div className="flex gap-2 mb-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDadosParaLimpar({
                              revenues: true, expenses: true, movimentacoes: true,
                              contas: true, compromissos: true, metas: true, roi: true
                            })}
                            className="text-zinc-400 border-zinc-700 hover:bg-white/10"
                            data-testid="limpar-dados-selecionar-tudo"
                          >
                            Selecionar Tudo
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDadosParaLimpar({
                              revenues: false, expenses: false, movimentacoes: false,
                              contas: false, compromissos: false, metas: false, roi: false
                            })}
                            className="text-zinc-400 border-zinc-700 hover:bg-white/10"
                            data-testid="limpar-dados-limpar-selecao"
                          >
                            Limpar Seleção
                          </Button>
                        </div>
                        
                        <p className="text-red-400 text-sm font-medium mb-4">
                          ⚠️ Esta ação não pode ser desfeita!
                        </p>
                        
                        <div className="space-y-3">
                          <div>
                            <Label className="text-zinc-400 text-sm">
                              Digite <strong className="text-white">CONFIRMAR</strong> para prosseguir:
                            </Label>
                            <Input
                              value={confirmacaoLimpar}
                              onChange={(e) => setConfirmacaoLimpar(e.target.value)}
                              placeholder="Digite CONFIRMAR"
                              className="mt-2 bg-zinc-900 border-zinc-700 text-white"
                              data-testid="limpar-dados-confirmacao-input"
                            />
                          </div>
                          
                          <Button
                            onClick={async () => {
                              if (!selectedGym) {
                                toast.error('Selecione uma empresa primeiro');
                                return;
                              }
                              if (confirmacaoLimpar !== 'CONFIRMAR') {
                                toast.error('Digite CONFIRMAR para prosseguir');
                                return;
                              }
                              const algumSelecionado = Object.values(dadosParaLimpar).some(v => v);
                              if (!algumSelecionado) {
                                toast.error('Selecione ao menos um tipo de dado para limpar');
                                return;
                              }
                              
                              setLimpandoDados(true);
                              try {
                                const params = new URLSearchParams();
                                Object.entries(dadosParaLimpar).forEach(([key, value]) => {
                                  params.append(key, value.toString());
                                });
                                const response = await api.delete(
                                  `/admin/limpar-dados-empresa/${selectedGym.id}?${params.toString()}`
                                );
                                toast.success(`Dados limpos com sucesso! ${response.data.total_registros_excluidos} registros excluídos.`);
                                setConfirmacaoLimpar('');
                              } catch (error) {
                                toast.error('Erro ao limpar dados: ' + (error.response?.data?.detail || error.message));
                              } finally {
                                setLimpandoDados(false);
                              }
                            }}
                            disabled={limpandoDados || confirmacaoLimpar !== 'CONFIRMAR' || !Object.values(dadosParaLimpar).some(v => v)}
                            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                            data-testid="limpar-dados-confirmar-button"
                          >
                            {limpandoDados ? (
                              <>
                                <Loader2 size={16} className="mr-2 animate-spin" />
                                Limpando...
                              </>
                            ) : (
                              <>
                                <Trash2 size={16} className="mr-2" />
                                Limpar Dados Selecionados
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ajuda e Tutorial */}
            {activeTab === 'help' && (
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <HelpCircle size={24} className="text-[var(--color-primary)]" />
                    Ajuda e Tutorial
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tour Guiado */}
                  <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-emerald-500/20">
                        <Play size={24} className="text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">Tour Guiado</h3>
                        <p className="text-zinc-400 text-sm mb-4">
                          Faça um tour interativo pelo sistema e aprenda a usar todas as funcionalidades principais.
                        </p>
                        <Button
                          onClick={() => {
                            startOnboarding();
                            toast.success('Tour iniciado! Siga as instruções na tela.');
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="restart-onboarding-button"
                        >
                          <Play size={16} className="mr-2" />
                          Iniciar Tour
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Guia de Preenchimento - Onde Lançar Cada Dado */}
                  <div>
                    <h3 className="text-white font-semibold mb-1">Onde Lançar Cada Tipo de Dado</h3>
                    <p className="text-zinc-500 text-sm mb-4">
                      Guia rápido para saber exatamente onde cadastrar cada informação financeira.
                    </p>
                    <div className="space-y-3">
                      <div
                        className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors"
                        data-testid="guia-preenchimento-venda"
                      >
                        <div className="p-2.5 rounded-lg bg-emerald-500/15 shrink-0">
                          <ShoppingCart size={20} className="text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium mb-1">Venda / Faturamento</p>
                          <p className="text-zinc-500 text-sm mb-2">
                            Registre em <strong className="text-zinc-300">Gestão de Receita</strong>, escolhendo a fonte e o perfil de recebimento (à vista, 30 dias, parcelado). A venda aparece automaticamente no DRE por competência.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/gestao-receita')}
                          className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 shrink-0"
                          data-testid="guia-preenchimento-venda-ir-button"
                        >
                          Ir <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>

                      <div
                        className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors"
                        data-testid="guia-preenchimento-despesa"
                      >
                        <div className="p-2.5 rounded-lg bg-red-500/15 shrink-0">
                          <Receipt size={20} className="text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium mb-1">Despesa Avulsa (aluguel, luz, salários, marketing)</p>
                          <p className="text-zinc-500 text-sm mb-2">
                            Lance no <strong className="text-zinc-300">DRE</strong>, marcando se é operacional (custo do dia a dia) ou não-operacional (investimento, retirada). Isso pertence ao mês em que a despesa ocorreu, não ao pagamento.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/dre')}
                          className="text-red-400 border-red-500/30 hover:bg-red-500/10 shrink-0"
                          data-testid="guia-preenchimento-despesa-ir-button"
                        >
                          Ir <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>

                      <div
                        className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors"
                        data-testid="guia-preenchimento-movimento-bancario"
                      >
                        <div className="p-2.5 rounded-lg bg-blue-500/15 shrink-0">
                          <Landmark size={20} className="text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium mb-1">Movimento Bancário (o que entrou/saiu do banco)</p>
                          <p className="text-zinc-500 text-sm mb-2">
                            Cadastre em <strong className="text-zinc-300">Caixa</strong>, manualmente ou importando seu extrato OFX. É a fonte de verdade do saldo real e da Projeção.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/caixa')}
                          className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10 shrink-0"
                          data-testid="guia-preenchimento-movimento-bancario-ir-button"
                        >
                          Ir <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>

                      <div
                        className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors"
                        data-testid="guia-preenchimento-financiamento"
                      >
                        <div className="p-2.5 rounded-lg bg-orange-500/15 shrink-0">
                          <FileText size={20} className="text-orange-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium mb-1">Financiamento / Parcelamento</p>
                          <p className="text-zinc-500 text-sm mb-2">
                            Cadastre em <strong className="text-zinc-300">Compromissos</strong>. As parcelas futuras entram na Projeção; parcelas já pagas aparecem no extrato real do Caixa, sem duplicidade.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/compromissos')}
                          className="text-orange-400 border-orange-500/30 hover:bg-orange-500/10 shrink-0"
                          data-testid="guia-preenchimento-financiamento-ir-button"
                        >
                          Ir <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Dicas Rápidas */}
                  <div>
                    <h3 className="text-white font-semibold mb-4">Dicas Rápidas</h3>
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white font-medium mb-1">📊 Cadastre seus dados primeiro</p>
                        <p className="text-zinc-500 text-sm">Quanto mais dados você cadastrar (faturamento, despesas), mais precisas serão as análises.</p>
                      </div>
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white font-medium mb-1">🤖 Use o Diretor Financeiro IA</p>
                        <p className="text-zinc-500 text-sm">Pergunte qualquer coisa sobre suas finanças. A IA analisa seus dados e dá recomendações.</p>
                      </div>
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white font-medium mb-1">📱 Acesse pelo celular</p>
                        <p className="text-zinc-500 text-sm">O sistema funciona perfeitamente no celular. Consulte seus dados de qualquer lugar.</p>
                      </div>
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white font-medium mb-1">📈 Acompanhe a Saúde Financeira</p>
                        <p className="text-zinc-500 text-sm">A página de Saúde Financeira mostra uma visão completa: caixa, carteira, compromissos e indicadores.</p>
                      </div>
                    </div>
                  </div>

                  {/* Contato */}
                  <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <p className="text-white font-medium mb-2">Precisa de ajuda?</p>
                    <p className="text-zinc-400 text-sm">
                      Entre em contato pelo WhatsApp para suporte técnico ou dúvidas sobre o sistema.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
